import _ from 'lodash'
import axios from 'axios'
import * as Sentry from '@sentry/browser'
import * as CURRENCY_SYMBOL_MAP from 'currency-symbol-map/map'
import { firestore } from 'firebase/app'

const convrrtV3API = axios.create({
  baseURL: 'https://us-central1-convrrt-v3.cloudfunctions.net/api/'
})

const db = firestore()
const shoppingCarts = db.collection('shoppingCarts')

function updateItemTotal ({
  product,
  currencySymbol
}) {
  const total = (product.qty || 1) * (product.sku.price / 100)
  const totalFixed = total.toFixed(2)
  return `${currencySymbol}${totalFixed}`
}

export default {
  namespaced: true,
  state: {
    cart: null,
    products: [],
    totalInCents: 0,
    total: '$0.00',
    cartRef: null,
    productsRef: null,
    unsubscribe: null,
    processing: false,
    currencyCode: 'USD',
    currencySymbol: CURRENCY_SYMBOL_MAP.USD
  },
  mutations: {
    updateCart (state, snap) {
      const cart = snap.data()
      const { orgID, projectID } = cart
      Sentry.setTags({ orgID, projectID })
      return state.cart = cart
    },
    updateProducts (state, products) {
      state.products = products.map(product => {
        product.total = updateItemTotal({
          product,
          currencySymbol: state.currencySymbol
        })
        return product
      })
      state.totalInCents = _.sumBy(state.products, p => p.sku.price)
    },
    startProcessing (state) {
      return state.processing = true
    },
    stopProcessing (state) {
      return state.processing = false
    },
    updateCurrency (state, code) {
      state.currencyCode = code
      state.currencySymbol = CURRENCY_SYMBOL_MAP[code]
    }
  },

  actions: {
    async fetch ({ state, commit, dispatch }, cartID) {
      const { currency_code: currencyCode } = await dispatch('getCurrencyCode', cartID)
      commit('updateCurrency', currencyCode)
      state.cartRef = shoppingCarts.doc(cartID)
      state.productsRef = state.cartRef.collection('products')
      commit('updateCart', await state.cartRef.get())
      commit('updateProducts', await state.productsRef.get().then(querySnap => querySnap.docs.map(snap => snap.data())))
      return { cart: state.cart, products: state.products }
    },
    async getCurrencyCode ({ state }, cartID) {
      const { data } = await convrrtV3API.get(`/checkout/${cartID}/currency-code`)
      return data
    },
    async watch ({ state, commit, dispatch }) {
      _.result(state, 'unsubscribe')

      const logErrorsAsDebug = (err) => console.debug(err)

      const unwatchCart = state.cartRef.onSnapshot(snap => commit('updateCart', snap), logErrorsAsDebug)
      const unwatchProducts = state.productsRef.onSnapshot(querySnap => {
        commit('updateProducts', querySnap.docs.map(snap => snap.data()))
        dispatch('recalculateTotal')
      }, logErrorsAsDebug)

      state.unsubscribe = () => {
        unwatchCart()
        unwatchProducts()
      }
    },

    async updateCurrency ({ state, commit, dispatch }, code) {
      commit('updateCurrency', code)
      commit('updateProducts', state.products)
      return dispatch('recalculateTotal')
    },

    async recalculateTotal ({ state }) {
      const total = state.totalInCents / 100
      const totalFixed = total.toFixed(2)
      state.total = `${state.currencySymbol}${totalFixed}`
    },

    async updateDetails ({ state }, data) {
      return state.cartRef.update(data)
    },

    async removeProduct ({ state }, productID) {
      return state.productsRef.doc(productID).remove()
    },

    async syncCart ({ state }) {
      const { data } = await convrrtV3API.post(`/checkout/${state.cartRef.id}/syncContact`)
      return data
    },

    async getCreditCardID ({ state, dispatch, rootState }, { creditCard, billingAddress, token }) {
      const infusionsoft = axios.create({
        baseURL: `https://${state.cart.projectID}.${rootState.site.site.crmHostname}`
      })

      try {
        const { data } = await infusionsoft.post('/app/creditCardSubmission/creditCard', {
          token: token,
          cardNumber: creditCard.number,
          cardType: creditCard.type,
          verificationCode: creditCard.cvc,
          expirationMonth: creditCard.expiry.split('/').shift(),
          expirationYear: creditCard.expiry.split('/').pop(),
          nameOnCard: creditCard.name,

          billZip: creditCard.zip,
          billName: creditCard.name,

          // Incorporate Billing Address into CC
          billAddress1: billingAddress && billingAddress.line1,
          billAddress2: billingAddress && billingAddress.line2,
          billCity: billingAddress && billingAddress.city,
          billState: billingAddress && billingAddress.state,
          billCountry: billingAddress && billingAddress.country
        })

        const { creditCardSubmissionToken } = data
        const { creditCardId } = creditCardSubmissionToken

        return dispatch('updateDetails', {
          creditCardID: creditCardId
        })
      } catch (err) {
        const { response = {} } = err
        const { data } = response

        if (!data.success) {
          const err = new Error('Failed credit card submission')
          err.response = { data: { errors: [{ message: data.message }] } }
          throw err
        }

        throw err
      }
    },

    async submitOrder ({ state, commit, dispatch }, { creditCard, billingAddress }) {
      try {
        commit('startProcessing')
        const { token } = await dispatch('syncCart')
        await dispatch('getCreditCardID', { creditCard, billingAddress, token })
        const response = await convrrtV3API.post(`/checkout/${state.cartRef.id}/submitOrder`)
        return response
      } finally {
        commit('stopProcessing')
      }
    }
  }
}
